// Capture screen — booth #, photos, quick fields → IndexedDB, instantly, offline.
// Big touch targets; the only required field is booth_number (the Feb lesson:
// over-built forms go unused). Everything else is optional and fast.
//
// v1.1 SAVE-AND-STAY. Save used to persist and immediately navigate to the Booths
// list, so you couldn't keep working the booth you just logged (Trip-1 friction:
// you'd save, realise you wanted one more photo, and have to go find the booth
// again). Now the first Save creates the record and KEEPS you on it; every later
// Save UPDATES that same record through `updateBooth` — never a second uuid — and
// "+ New booth" is the explicit reset for the next capture.
//
// The re-save deliberately re-reads the record from IndexedDB rather than pushing
// the in-memory copy back: while you sit on this screen the sync engine may have
// uploaded the photos and written `blob_url`/`synced` onto the very refs we hold,
// and a blind write of the stale copy would null them back out and orphan the
// uploaded blobs.

import { SHOWS, PRIORITIES, type Show, type Priority } from '../config';
import type { BoothRecord, PhotoRef } from '../db/schema';
import { photoBlob } from '../db/schema';
import { saveBooth, updateBooth, getBooth, deleteLocalPhoto, type DraftPhoto } from '../db/booths';
import { compressToStoredPhoto, newId } from './photo';
import { getIdentity, attributionName } from '../auth/auth';
import { toast } from '../ui/toast';
import { confirmDialog } from '../ui/confirm';
import { populateShowSelect } from '../ui/showSelect';
import { markThumbNote } from '../ui/thumbNote';
import { getLastShow, setLastShow } from '../config/shows';
import { openLightbox, type LightboxPhoto } from '../detail/lightbox';
import { takeCapturePrefill } from './prefill';

interface PendingPhoto {
  draft: DraftPhoto;
  objectUrl: string;
  /** v1.1 per-photo note (free text). '' = none. */
  note: string;
  /** True once this photo's PhotoRef is on the saved booth record. */
  saved: boolean;
}

export function renderCapture(root: HTMLElement, onSaved: () => void): void {
  let pending: PendingPhoto[] = [];
  // v0.7: set when Capture was opened from the exhibitor directory ("Capture this
  // booth"). Consumed once — a later visit to the tab gets a blank form.
  const prefill = takeCapturePrefill();

  root.innerHTML = `
    <form id="capture-form" class="card" novalidate>
      <div class="saved-bar" id="saved-bar" hidden>
        <span class="saved-bar__dot">✓</span>
        <span class="saved-bar__text">Saved — editing booth <strong id="saved-bar-num"></strong></span>
      </div>

      <label class="field">
        <span>Show</span>
        <select name="show" class="control" id="show-select">
          ${SHOWS.map((s) => `<option value="${s}">${s}</option>`).join('')}
        </select>
      </label>

      <label class="field">
        <span>Booth # <em>required</em></span>
        <input name="booth_number" class="control" inputmode="text"
               autocapitalize="characters" placeholder="e.g. 1423" required
               value="${esc(prefill?.booth_number ?? '')}" />
      </label>

      <label class="field">
        <span>Vendor name</span>
        <input name="vendor_name" class="control" placeholder="Vendor / brand"
               value="${esc(prefill?.vendor_name ?? '')}" />
      </label>

      <div class="field">
        <span>Photos</span>
        <div id="thumbs" class="thumbs"></div>
        <label class="btn btn--photo">
          + Add photo
          <input id="photo-input" type="file" accept="image/*" capture="environment"
                 multiple hidden />
        </label>
      </div>

      <div class="field">
        <span>Priority</span>
        <div class="segmented" id="priority">
          ${PRIORITIES.map(
            (p, i) =>
              `<button type="button" class="seg ${i === 0 ? 'seg--on' : ''}" data-priority="${p}">${p}</button>`,
          ).join('')}
        </div>
      </div>

      <label class="field field--row">
        <span>Samples taken</span>
        <input name="samples_taken" type="checkbox" class="switch" />
      </label>

      <label class="field">
        <span>Summary</span>
        <input name="summary" class="control" placeholder="One line: what they sell" />
      </label>

      <label class="field">
        <span>Ships from</span>
        <input name="ships_from" class="control" placeholder="City / country / warehouse" />
      </label>

      <label class="field">
        <span>Vendor contact</span>
        <input name="vendor_contact" class="control" placeholder="Name / email / phone" />
      </label>

      <label class="field">
        <span>Notes</span>
        <textarea name="notes" class="control" rows="3" placeholder="Anything worth remembering"></textarea>
      </label>

      <details class="more">
        <summary class="more__summary"><span class="more__summary-text">More details</span></summary>
        <div class="more__body">
          <label class="field">
            <span>Case packs</span>
            <input name="case_packs" class="control" placeholder="e.g. 12 per case" />
          </label>
          <label class="field">
            <span>Size run</span>
            <input name="size_run" class="control" placeholder="e.g. S–XXL" />
          </label>
          <label class="field">
            <span>Total spent</span>
            <input name="total_spent" class="control" placeholder="e.g. $1,200" />
          </label>
        </div>
      </details>

      <button type="submit" class="btn btn--primary btn--save" id="save-btn">Save booth ✓</button>
      <button type="button" class="btn btn--new" id="new-booth" hidden>+ New booth</button>
    </form>
  `;

  const form = root.querySelector<HTMLFormElement>('#capture-form')!;
  const thumbs = root.querySelector<HTMLDivElement>('#thumbs')!;
  const photoInput = root.querySelector<HTMLInputElement>('#photo-input')!;
  const showSelect = root.querySelector<HTMLSelectElement>('#show-select')!;
  const saveBtn = root.querySelector<HTMLButtonElement>('#save-btn')!;
  const newBtn = root.querySelector<HTMLButtonElement>('#new-booth')!;
  const savedBar = root.querySelector<HTMLElement>('#saved-bar')!;
  const savedBarNum = root.querySelector<HTMLElement>('#saved-bar-num')!;
  let priority: Priority = PRIORITIES[0];

  // The booth being captured. `savedId` is null until the first successful Save;
  // from then on this screen is EDITING that record.
  let boothId = newId();
  let savedId: string | null = null;
  let savedRecord: BoothRecord | null = null;
  /** Serialised form at the moment of the last save — the "is it dirty?" baseline. */
  let savedSnapshot = '';

  // Controlled show dropdown (defaults ∪ custom ∪ booth-seen + "+ Add show…").
  // A directory prefill selects its show; it's always one of the controlled values
  // (the build step maps the data's show codes onto SHOWS), so this can't smuggle a
  // free-typed show past the v0.5 typo guard. v1.1: with no prefill the default is
  // the STICKY last-captured show (persisted per device), falling back to the first
  // option when there's no history or the remembered show is gone from the list —
  // populateShowSelect already does that fallback.
  void populateShowSelect(showSelect, prefill?.show || getLastShow() || SHOWS[0]);

  // Prefilled from the directory: the vendor + booth are known, so put the cursor
  // where the user actually has something to add rather than at the top of the form.
  if (prefill) {
    const summaryEl = root.querySelector<HTMLInputElement>('input[name="summary"]');
    if (prefill.booth_number) summaryEl?.focus();
    else root.querySelector<HTMLInputElement>('input[name="booth_number"]')?.focus();
  }

  /** Everything the user typed, as one string — cheap dirty-check for "New booth". */
  function serializeForm(): string {
    const d = new FormData(form);
    const fields = [...d.entries()].map(([k, v]) => `${k}=${String(v)}`).join('');
    return `${fields}${priority}${pending.map((p) => p.note).join('')}`;
  }

  // Render pending thumbnails from the `pending` array, each with a ✕ remove
  // affordance. Rebuilding wholesale keeps the DOM in lockstep with the array,
  // so removing the middle photo can't leave a stale thumb.
  function renderThumbs(): void {
    thumbs.innerHTML = '';
    pending.forEach((p) => {
      const wrap = document.createElement('div');
      wrap.className = 'thumb';
      const kb = Math.round(p.draft.stored.bytes / 1024);
      wrap.innerHTML = `<img src="${p.objectUrl}" alt="booth photo" /><span class="thumb__kb">${kb} KB</span><button type="button" class="thumb__x" aria-label="Remove photo">✕</button>`;
      markThumbNote(wrap, !!p.note);
      wrap.querySelector('.thumb__x')!.addEventListener('click', async (e) => {
        e.preventDefault();
        e.stopPropagation(); // don't also open the viewer
        // Global rule: every removal goes through the 2-second confirm.
        const ok = await confirmDialog({ title: 'Remove this photo?', confirmLabel: 'Remove' });
        if (!ok) return;
        await removePhoto(p);
      });
      // Tap the picture → fullscreen viewer (where the per-photo note lives).
      wrap.querySelector('img')!.addEventListener('click', () => openViewer(p));
      thumbs.appendChild(wrap);
    });
  }

  /**
   * Drop a photo. Pre-save that's just an array splice; once the booth exists the
   * ref has to come OFF the record (and its bytes out of the photos store) or the
   * booth keeps pointing at an image the user removed — and its note goes with it,
   * because the note lives on the ref.
   */
  async function removePhoto(p: PendingPhoto): Promise<void> {
    URL.revokeObjectURL(p.objectUrl);
    const idx = pending.indexOf(p);
    if (idx >= 0) pending.splice(idx, 1);
    renderThumbs();
    if (p.saved && savedId) {
      const localId = p.draft.stored.local_id;
      const current = (await getBooth(savedId)) ?? savedRecord;
      if (current) {
        current.photos = current.photos.filter((r) => r.local_id !== localId);
        await deleteLocalPhoto(localId);
        await updateBooth(current);
        savedRecord = current;
        savedSnapshot = serializeForm();
        onSaved();
      }
    }
  }

  /** Open the fullscreen viewer starting on `p`, with per-photo notes editable. */
  function openViewer(p: PendingPhoto): void {
    const items: LightboxPhoto[] = pending.map((q) => ({
      url: q.objectUrl,
      localId: q.draft.stored.local_id,
      note: q.note,
    }));
    const start = Math.max(0, pending.indexOf(p));
    openLightbox(items, start, {
      onNoteSave: async (localId, note) => {
        const target = pending.find((q) => q.draft.stored.local_id === localId);
        if (!target) return;
        target.note = note;
        renderThumbs();
        // Already-saved photo → persist the note now rather than waiting for the
        // next Save; a note typed in the viewer shouldn't need a second action to
        // survive. Unsaved photos carry it in memory until the first Save.
        if (target.saved && savedId) {
          const current = (await getBooth(savedId)) ?? savedRecord;
          if (!current) return;
          const ref = current.photos.find((r) => r.local_id === localId);
          if (!ref) return;
          ref.note = note || undefined;
          await updateBooth(current);
          savedRecord = current;
          savedSnapshot = serializeForm();
          onSaved();
        }
      },
    });
  }

  // Priority segmented control.
  root.querySelectorAll<HTMLButtonElement>('#priority .seg').forEach((btn) => {
    btn.addEventListener('click', () => {
      root.querySelectorAll('#priority .seg').forEach((b) => b.classList.remove('seg--on'));
      btn.classList.add('seg--on');
      priority = btn.dataset.priority as Priority;
    });
  });

  // Photo capture: compress on-device, hold the blob, show a thumbnail.
  photoInput.addEventListener('change', async () => {
    const files = Array.from(photoInput.files ?? []);
    photoInput.value = ''; // allow re-selecting the same file
    for (const file of files) {
      const createdAt = new Date().toISOString();
      try {
        // Photos added after the first Save still belong to THIS booth (boothId is
        // the saved record's id), so they attach on the next Save, not to a new one.
        const stored = await compressToStoredPhoto(file, boothId, 'product', createdAt);
        const objectUrl = URL.createObjectURL(photoBlob(stored));
        pending.push({ draft: { stored }, objectUrl, note: '', saved: false });
        renderThumbs();
      } catch (err) {
        console.error(err);
        toast('Photo failed — try again', 'warn');
      }
    }
  });

  /**
   * Reflect "this screen is now editing a saved booth" in the chrome. Only the
   * photos that actually went into this write are marked saved — a photo the user
   * added while the save was in flight isn't on the record yet and must stay
   * pending, or the next Save would skip it and lose it.
   */
  function markSaved(record: BoothRecord, justWritten: PendingPhoto[]): void {
    savedId = record.id;
    savedRecord = record;
    savedBarNum.textContent = `#${record.booth_number}`;
    savedBar.hidden = false;
    newBtn.hidden = false;
    saveBtn.textContent = 'Save changes ✓';
    justWritten.forEach((p) => (p.saved = true));
    savedSnapshot = serializeForm();
  }

  /** Blank the form for the next booth, keeping the (sticky) Show selection. */
  function resetForm(): void {
    pending.forEach((p) => URL.revokeObjectURL(p.objectUrl));
    pending = [];
    renderThumbs();
    const show = showSelect.value; // sticky: the next booth is almost always same show
    form.reset();
    showSelect.value = show;
    priority = PRIORITIES[0];
    root.querySelectorAll('#priority .seg').forEach((b, idx) =>
      b.classList.toggle('seg--on', idx === 0),
    );
    root.querySelector<HTMLDetailsElement>('.more')!.open = false;
    boothId = newId();
    savedId = null;
    savedRecord = null;
    savedSnapshot = '';
    savedBar.hidden = true;
    newBtn.hidden = true;
    saveBtn.textContent = 'Save booth ✓';
    window.scrollTo(0, 0);
    root.querySelector<HTMLInputElement>('input[name="booth_number"]')?.focus();
  }

  newBtn.addEventListener('click', async () => {
    // The saved booth is safe on disk; only edits made SINCE the last save would be
    // lost, so only ask when there are any.
    const dirty = serializeForm() !== savedSnapshot || pending.some((p) => !p.saved);
    if (dirty) {
      const ok = await confirmDialog({
        title: 'Start a new booth?',
        message: 'Changes made since the last save will be lost.',
        confirmLabel: 'New booth',
      });
      if (!ok) return;
    }
    resetForm();
    toast('New booth');
  });

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const data = new FormData(form);
    const boothNumber = String(data.get('booth_number') ?? '').trim();
    if (!boothNumber) {
      toast('Booth # is required', 'warn');
      return;
    }

    const show = String(data.get('show') ?? SHOWS[0]) as Show;
    const fields = {
      show,
      booth_number: boothNumber,
      vendor_name: String(data.get('vendor_name') ?? '').trim(),
      priority,
      summary: String(data.get('summary') ?? '').trim(),
      samples_taken: data.get('samples_taken') === 'on',
      vendor_contact: String(data.get('vendor_contact') ?? '').trim(),
      notes: String(data.get('notes') ?? '').trim(),
      ships_from: String(data.get('ships_from') ?? '').trim(),
      case_packs: String(data.get('case_packs') ?? '').trim(),
      size_run: String(data.get('size_run') ?? '').trim(),
      total_spent: String(data.get('total_spent') ?? '').trim(),
    };
    const newPhotos = pending.filter((p) => !p.saved);
    const refFor = (p: PendingPhoto): PhotoRef => ({
      local_id: p.draft.stored.local_id,
      kind: p.draft.stored.kind,
      blob_url: null,
      synced: false,
      note: p.note || undefined,
    });

    try {
      if (!savedId) {
        const now = new Date().toISOString();
        const me = getIdentity();
        const record: BoothRecord = {
          id: boothId,
          ...fields,
          // Store the DISPLAY name for creator attribution (e.g. "Brandon Penticuff"),
          // not the login — SSS logins are first-initial+lastname, so a first name
          // can't be parsed from the email. Signed-out/stub keep prior behaviour.
          // (On sync the server re-asserts this from the validated token's name claim.)
          // v1.2: attributionName() also covers an EXPIRED session — the account
          // object still names the user, so a dead refresh token must not quietly
          // start stamping booths with the login email.
          captured_by: attributionName(me),
          group: '',
          captured_at: now,
          updated_at: now,
          photos: newPhotos.map(refFor),
          // v0.6: Category is no longer entered in the UI (field kept in the schema and
          // on existing records). A new booth simply starts with none.
          category_tags: [],
          matched_vendor: null,
          sync_status: 'local',
        };
        await saveBooth(record, newPhotos.map((p) => p.draft));
        markSaved(record, newPhotos);
      } else {
        // Re-read: the sync engine may have stamped blob_url/synced onto these refs
        // while the screen sat open. Merge the form over the CURRENT record.
        const current = (await getBooth(savedId)) ?? savedRecord!;
        const updated: BoothRecord = {
          ...current,
          ...fields,
          photos: [
            // Notes are edited on this screen, so they win; everything else on an
            // existing ref (blob_url, synced) is carried through untouched.
            ...current.photos.map((r) => {
              const p = pending.find((q) => q.draft.stored.local_id === r.local_id);
              return p ? { ...r, note: p.note || undefined } : r;
            }),
            ...newPhotos.map(refFor),
          ],
        };
        await updateBooth(updated, newPhotos.map((p) => p.draft));
        markSaved(updated, newPhotos);
      }
      // Sticky Show: remember what we just captured under, for the next launch.
      setLastShow(show);
      toast('Saved ✓ (on device)');
      onSaved(); // refresh the pending-sync badge; we deliberately STAY on the booth
    } catch (err) {
      // Surface the real error on-device: an iPad has no desktop console, so the
      // name+message is the only way to diagnose a WebKit IndexedDB failure here.
      console.error('saveBooth failed', err);
      const detail = err instanceof Error ? `${err.name}: ${err.message}` : String(err);
      toast(`Save failed (${detail}) — data kept on screen`, 'warn');
    }
  });
}

// Prefill values are interpolated into `value="…"` attributes above; exhibitor
// names are third-party data (an apostrophe or & is common), so they're escaped.
function esc(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!,
  );
}
